import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Crown, GraduationCap, Loader2, Medal, PenTool, User } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  fetchStudentLeaderboard,
  fetchCreatorLeaderboard,
  fetchMyStudentRank,
  fetchMyCreatorRank,
  type LeaderboardRow,
} from "@/lib/db";

/** Extract a user-facing error string from Supabase/Postgrest error or unknown. */
function getErrorMessage(err: unknown): string {
  if (err == null) return "Unknown error";
  const e = err as { message?: string; details?: string; hint?: string; code?: string };
  return e.message || e.details || String(err);
}

type TabKind = "students" | "creators";

interface LeaderboardEntry {
  rank: number;
  username: string;
  score: number;
  avatarUrl?: string | null;
}

function rowToEntry(r: LeaderboardRow): LeaderboardEntry {
  return {
    rank: Number(r.rank) || 0,
    username: r.username ?? "—",
    score: Number(r.score) || 0,
    avatarUrl: r.avatar_url ?? null,
  };
}

/** Generate deterministic avatar placeholder (no external service) */
function avatarPlaceholder(username: string, size: number): string {
  const hash = username.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const hue = hash % 360;
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <rect fill="hsl(${hue}, 45%, 35%)" width="${size}" height="${size}" rx="${size / 2}"/>
      <text x="50%" y="50%" dominant-baseline="central" text-anchor="middle" fill="rgba(255,255,255,0.9)" font-size="${size * 0.4}" font-family="system-ui,sans-serif">${(username[0] || "?").toUpperCase()}</text>
    </svg>`
  )}`;
}

const GOLD = "hsl(45, 90%, 52%)";
const SILVER = "hsl(0, 0%, 75%)";
const BRONZE = "hsl(30, 60%, 50%)";

export default function Leaderboard() {
  const { t } = useTranslation();
  const { user, profile, avatarUrl } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlSub = searchParams.get("sub");
  const tab: TabKind = urlSub === "creators" ? "creators" : "students";

  const setTab = (next: TabKind) => {
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set("tab", "leaderboard");
        nextParams.set("sub", next);
        return nextParams;
      },
      { replace: true }
    );
  };

  const { data: studentData, isLoading: studentsLoading, error: studentsError } = useQuery({
    queryKey: ["leaderboard", "students"],
    queryFn: fetchStudentLeaderboard,
    refetchOnMount: "always",
  });
  const { data: creatorData, isLoading: creatorsLoading, error: creatorsError } = useQuery({
    queryKey: ["leaderboard", "creators", user?.id],
    queryFn: () => fetchCreatorLeaderboard(user?.id),
    refetchOnMount: "always",
  });
  const { data: myStudentRank } = useQuery({
    queryKey: ["leaderboard", "my-student-rank", user?.id],
    queryFn: () => fetchMyStudentRank(user!.id),
    enabled: !!user?.id,
    refetchOnMount: "always",
  });
  const { data: myCreatorRank } = useQuery({
    queryKey: ["leaderboard", "my-creator-rank", user?.id],
    queryFn: () => fetchMyCreatorRank(user!.id),
    enabled: !!user?.id,
    refetchOnMount: "always",
  });

  const studentList = useMemo(() => (studentData ?? []).map(rowToEntry), [studentData]);
  const creatorList = useMemo(() => (creatorData ?? []).map(rowToEntry), [creatorData]);

  const list = tab === "students" ? studentList : creatorList;
  const top3 = list.slice(0, 3);
  const rest = list.slice(3, 100);

  const isLoading = tab === "students" ? studentsLoading : creatorsLoading;
  const error = tab === "students" ? studentsError : creatorsError;

  // Log Supabase/query errors to console for debugging (e.g. missing RPC, missing columns)
  useEffect(() => {
    if (studentsError) {
      console.error("Supabase Error (students leaderboard):", studentsError);
    }
    if (creatorsError) {
      console.error("Supabase Error (creators leaderboard):", creatorsError);
    }
  }, [studentsError, creatorsError]);

  const currentUserRank = tab === "students"
    ? (myStudentRank?.rank ?? null)
    : (myCreatorRank?.rank ?? null);
  const currentUserScore = tab === "students"
    ? (myStudentRank?.score ?? 0)
    : (myCreatorRank?.score ?? 0);
  const scoreLabel = tab === "students" ? t("leaderboard.crystals") : t("leaderboard.cardsCreated");

  const formatScore = (n: number) => {
    const v = n ?? 0;
    if (!Number.isFinite(v)) return "0";
    return v >= 1000 ? v.toLocaleString() : String(v);
  };
  const safeScore = (value: number | undefined | null) => formatScore(value ?? 0);
  const safeRank = (value: number | undefined | null) =>
    (value != null && Number.isFinite(value) ? value : 0);
  const displayRank = (currentUserRank != null && Number.isFinite(currentUserRank))
    ? String(currentUserRank)
    : "0";
  const displayScore = safeScore(currentUserScore);

  if (error) {
    const errorMessage = getErrorMessage(error);
    return (
      <div className="leaderboard-page" style={{ maxWidth: 900, margin: "0 auto", fontFamily: "DM Sans, sans-serif" }}>
        <p style={{ color: "hsl(var(--destructive))", marginTop: 16 }}>
          {t("leaderboard.error", "Failed to load leaderboard. Try again later.")}
        </p>
        <p style={{ color: "hsl(var(--muted-foreground))", marginTop: 8, fontSize: 13 }}>
          Failed to load: {errorMessage}
        </p>
      </div>
    );
  }

  return (
    <div
      className="leaderboard-page"
      style={{
        maxWidth: 900,
        margin: "0 auto",
        fontFamily: "DM Sans, sans-serif",
      }}
    >
      <h1
        className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white mb-2 flex items-center justify-center md:justify-start gap-2 md:gap-2.5 w-full"
        style={{ fontFamily: "DM Sans, sans-serif", color: "hsl(var(--foreground))" }}
      >
        <Medal className="w-6 h-6 md:w-7 md:h-7 shrink-0" strokeWidth={2} style={{ color: "hsl(var(--primary))" }} />
        {t("leaderboard.title")}
      </h1>

      {/* Segmented control: Top Students / Top Creators — full width on mobile */}
      <div
        className="flex w-full md:w-fit gap-2 p-1 rounded-lg mb-8"
        style={{
          background: "hsl(var(--bg3))",
        }}
      >
        <button
          type="button"
          onClick={() => setTab("students")}
          className={`flex-1 md:flex-initial flex items-center justify-center gap-2 py-2.5 px-4 md:px-6 rounded-md text-[0.875rem] font-medium transition-all ${
            tab === "students" ? "!text-white" : ""
          }`}
          style={{
            background: tab === "students" ? "hsl(var(--primary))" : "transparent",
            color: tab === "students" ? "#ffffff" : "hsl(var(--foreground))",
            fontWeight: tab === "students" ? 600 : 500,
            fontFamily: "DM Sans, sans-serif",
          }}
        >
          <GraduationCap className={`w-4 h-4 shrink-0 ${tab === "students" ? "!text-white" : ""}`} stroke="currentColor" />
          {t("leaderboard.tabStudents")}
        </button>
        <button
          type="button"
          onClick={() => setTab("creators")}
          className={`flex-1 md:flex-initial flex items-center justify-center gap-2 py-2.5 px-4 md:px-6 rounded-md text-[0.875rem] font-medium transition-all ${
            tab === "creators" ? "!text-white" : ""
          }`}
          style={{
            background: tab === "creators" ? "hsl(var(--primary))" : "transparent",
            color: tab === "creators" ? "#ffffff" : "hsl(var(--foreground))",
            fontWeight: tab === "creators" ? 600 : 500,
            fontFamily: "DM Sans, sans-serif",
          }}
        >
          <PenTool className={`w-4 h-4 shrink-0 ${tab === "creators" ? "!text-white" : ""}`} stroke="currentColor" />
          {t("leaderboard.tabCreators")}
        </button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            padding: "48px 24px",
            marginBottom: 24,
            color: "hsl(var(--muted-foreground))",
            fontSize: 14,
          }}
        >
          <Loader2 size={20} strokeWidth={2} className="animate-spin" />
          <span>{t("leaderboard.loading", "Loading leaderboard…")}</span>
        </div>
      )}

      {/* Podium (Top 3) — hide when loading to avoid layout jump */}
      {!isLoading && (
      <div className="grid grid-cols-3 gap-2 md:gap-4 items-end mb-6 md:mb-10">
        {[1, 0, 2].map((idx) => {
          const entry = top3[idx];
          if (!entry) return null;
          const isFirst = entry.rank === 1;
          const isSecond = entry.rank === 2;
          const isThird = entry.rank === 3;
          const color = isFirst ? GOLD : isSecond ? SILVER : BRONZE;
          const avatarSizeDesktop = isFirst ? 88 : isSecond ? 72 : 64;
          const avatarClass = isFirst
            ? "w-10 h-10 md:w-[88px] md:h-[88px]"
            : isSecond
              ? "w-10 h-10 md:w-[72px] md:h-[72px]"
              : "w-10 h-10 md:w-16 md:h-16";
          return (
            <div
              key={entry.rank}
              className="flex flex-col items-center p-2 md:p-6 rounded-lg"
              style={{
                background: "hsl(var(--card))",
                border: `2px solid ${color}`,
                boxShadow: "0 8px 24px rgba(0,0,0,.25)",
                order: idx === 0 ? 2 : idx === 1 ? 1 : 3,
              }}
            >
              <div
                className={`${avatarClass} rounded-full overflow-hidden shrink-0 mb-1.5 md:mb-3 border-[3px]`}
                style={{ borderColor: color }}
              >
                <img
                  src={entry.avatarUrl || avatarPlaceholder(entry.username, avatarSizeDesktop)}
                  alt={entry.username}
                  className="w-full h-full object-cover"
                />
              </div>
              {isFirst && <Crown className="w-5 h-5 md:w-6 md:h-6 shrink-0 mb-0.5 md:mb-1" style={{ color }} />}
              <span className="font-bold text-xs md:text-base text-slate-900 dark:text-white mb-0.5 md:mb-1 text-center truncate max-w-full">
                {entry.username}
              </span>
              <span
                className={`text-lg md:text-4xl font-extrabold mb-0.5 md:mb-1 ${
                  isFirst ? "text-green-600" : isSecond ? "text-orange-500" : "text-yellow-400"
                }`}
              >
                {safeRank(entry?.rank)}
              </span>
              <span
                className={`font-semibold text-xs md:text-base text-center ${
                  isFirst ? "text-green-600" : isSecond ? "text-orange-500" : "text-yellow-400"
                }`}
              >
                {safeScore(entry?.score)} {tab === "students" ? t("leaderboard.crystals") : t("leaderboard.cardsCreated")}
              </span>
            </div>
          );
        })}
      </div>
      )}

      {/* Current user sticky bar — solid bg-primary, pure white text. Avoid text-sm/text-xs (overridden by achievements-dashboard global CSS). */}
      <div
        className="sticky top-0 z-10 flex items-center gap-3 py-3.5 px-5 mb-5 rounded-lg bg-[hsl(var(--primary))] shadow-[0_4px_12px_rgba(109,90,255,.35)] text-white"
      >
        <div
          className="shrink-0 w-9 h-9 rounded-full overflow-hidden flex items-center justify-center"
          style={{ background: avatarUrl ? "transparent" : "rgba(255,255,255,.25)" }}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            <span className="text-white font-bold text-[1rem]">
              {(profile?.username ?? user?.email ?? "?").toString().charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex flex-col items-center justify-center text-center gap-1 flex-1 min-w-0">
          {user ? (
            <>
              <span className="text-white font-medium text-[0.875rem] leading-tight">
                {t("leaderboard.yourRankLabelRank", { rank: displayRank })}
              </span>
              <span className="text-white font-medium text-[0.875rem] leading-tight">
                {t("leaderboard.yourRankLabelScore", { scoreLabel, score: displayScore })}
              </span>
            </>
          ) : (
            <span className="text-white font-medium text-[0.875rem] leading-tight">
              {t("leaderboard.signInToSeeRank", "Sign in to see your rank")}
            </span>
          )}
        </div>
      </div>

      {/* Top 100 list (ranks 4–100) — hidden on mobile to save space */}
      {!isLoading && (
      <div
        className="hidden md:block"
        style={{
          background: "hsl(var(--card))",
          borderRadius: "var(--radius)",
          border: "1px solid hsl(var(--border))",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "56px 48px 1fr 120px",
            gap: 12,
            padding: "12px 16px",
            background: "hsl(var(--bg3))",
            fontWeight: 600,
            fontSize: 12,
            color: "hsl(var(--muted-foreground))",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          <span>{t("leaderboard.rank")}</span>
          <span aria-hidden style={{ width: 48 }} />
          <span>{t("leaderboard.username")}</span>
          <span style={{ textAlign: "right" }}>{scoreLabel}</span>
        </div>
        <div style={{ maxHeight: 420, overflowY: "auto" }}>
          {rest.length === 0 ? (
            <div
              style={{
                padding: "32px 16px",
                textAlign: "center",
                color: "hsl(var(--muted-foreground))",
                fontSize: 14,
              }}
            >
              {t("leaderboard_empty_state", "No one here yet. Be the first!")}
            </div>
          ) : (
            rest.map((entry) => (
              <div
                key={`${tab}-${entry.rank}-${entry.username}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "56px 48px 1fr 120px",
                  gap: 12,
                  alignItems: "center",
                  padding: "12px 16px",
                  borderBottom: "1px solid hsl(var(--border))",
                  fontSize: 14,
                  color: "hsl(var(--foreground))",
                }}
              >
                <span style={{ fontWeight: 600, color: "hsl(var(--muted-foreground))" }}>{safeRank(entry?.rank)}</span>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: "1px solid hsl(var(--border))",
                  }}
                >
                  <img
                    src={entry.avatarUrl || avatarPlaceholder(entry.username, 40)}
                    alt={entry.username}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
                <span style={{ fontWeight: 500 }}>{entry.username}</span>
                <span style={{ textAlign: "right", fontWeight: 600 }}>
                  {safeScore(entry?.score)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
      )}
    </div>
  );
}
